import { ApiError, AuthenticationRequiredError, NetworkError } from "../api/errors";
import type {
  AttributionRequestBody,
  AttributionResult,
  ClockStateResponse,
  EventRequestBody,
  EventResult,
  ProjectDay,
  WorkDayResponse,
} from "../api/schemas";
import type { BreakQualification } from "../domain/clock";
import { addDays, formatInstant, parseInstant, toLocalDate } from "../domain/time";
import type { LocalDate } from "../domain/time";
import { storedWorkTimeZone } from "../shared/session/roleSnapshot";
import { backoffDelayMs, defaultBackoffPolicy } from "./backoff";
import type { BackoffPolicy } from "./backoff";
import { settingKeys } from "./LocalStore";
import type { LocalStore } from "./LocalStore";
import type { LocalAttribution, LocalEvent } from "./model";
import { isOwnEvent } from "./ownEvents";
import type { SyncApi } from "./SyncApi";

/**
 * How long confirmed attributions are kept for display. The server's project view may not show a
 * switch yet (a switch in the same second as the snapshot is a block of zero length), so a
 * confirmed switch is never dropped with the snapshot that confirmed it.
 */
const attributionDisplayMs = 2 * 86_400_000;

/**
 * Longest range of `GET /me/workdays` and `GET /me/project-days` in days (server limits
 * `WorkDayQuery.MaxRangeDays` and `ProjectDayQuery.MaxRangeDays`).
 */
const maxSnapshotRangeDays = 93;

/**
 * Returns the first day of the snapshot: yesterday (night shifts), or the oldest day the server
 * reports open if it began earlier. The server returns a working day only for a range that
 * contains its start date (ADR-0007), so a shift open since Friday is missing from Sunday's
 * "yesterday and today" and the clock would show the person clocked out (F-11, ADR-0126). Open
 * days beyond the server's range limit are left out.
 *
 * @param today - Today's local date.
 * @param openDays - Local dates of the open working days reported by the server.
 * @returns The first local date to request.
 */
export function snapshotRangeStart(today: LocalDate, openDays: readonly LocalDate[]): LocalDate {
  const earliestAllowed = addDays(today, 1 - maxSnapshotRangeDays);
  const start = openDays.reduce(
    (earliest, date) => (date < earliest && date >= earliestAllowed ? date : earliest),
    addDays(today, -1),
  );

  return start;
}

/**
 * Returns the start dates of the shifts the next snapshot has to load again although the server no
 * longer reports them open (F-104): a shift of the previous snapshot that was still open or ended
 * yesterday or later, and the days of own events still on the device. The server returns a working day
 * only for a range containing its start date, so a shift that began before yesterday and whose
 * clock-out the server just accepted would otherwise drop out of the snapshot while its confirmed
 * events are pruned, and vanish from the device.
 *
 * @param previous - Working days of the previous snapshot.
 * @param events - Events stored on the device.
 * @param yesterday - Yesterday's local date.
 * @param timeZone - Time zone of the snapshot's days.
 * @returns The dates to keep in the range (unordered, possibly repeated).
 */
export function carriedSnapshotDays(
  previous: readonly WorkDayResponse[],
  events: readonly LocalEvent[],
  yesterday: LocalDate,
  timeZone: string,
): LocalDate[] {
  const fromSnapshot = previous
    .filter((day) => !day.isClosed || toLocalDate(parseInstant(day.lastEnd), timeZone) >= yesterday)
    .map((day) => day.date);
  const fromEvents = events
    .filter((event) => isOwnEvent(event) && event.syncStatus !== "rejected")
    .map((event) => toLocalDate(event.occurredAt, timeZone));
  const carried = [...fromSnapshot, ...fromEvents];

  return carried;
}

/**
 * Returns the last day of the snapshot: today, or the latest day the server reports open if it lies
 * after today. The server's open days are dates in the time zone of the establishment; while the app
 * does not know that zone yet (no profile stored on the device), "today" is taken in the device's zone
 * and an open day can lie after it, for example at 01:00 in Berlin on a device set to New York. Without
 * this end it would never be loaded and the clock would show the person clocked out (F-18, ADR-0132).
 *
 * @param today - Today's local date.
 * @param openDays - Local dates of the open working days reported by the server.
 * @returns The last local date to request.
 */
export function snapshotRangeEnd(today: LocalDate, openDays: readonly LocalDate[]): LocalDate {
  const latestAllowed = addDays(today, maxSnapshotRangeDays - 1);
  const end = openDays.reduce(
    (latest, date) => (date > latest && date <= latestAllowed ? date : latest),
    today,
  );

  return end;
}

/** Phase of the synchronization. */
export type SyncPhase = "idle" | "syncing" | "offline" | "error" | "signedOut";

/** Observable state of the synchronization. */
export interface SyncStatus {
  readonly phase: SyncPhase;
  /** Device time of the last complete synchronization in milliseconds. */
  readonly lastSuccessAtMs: number | null;
  /** Device time of the next automatic attempt, if one is scheduled. */
  readonly nextAttemptAtMs: number | null;
  /** Error code of the last failure (API code or HTTP status), if any. */
  readonly lastErrorCode: string | null;
  /**
   * Whether the last run sent every entry of the outbox to the server, also when only refreshing the
   * snapshot failed afterwards: a failed refresh never reads as entries not sent (F-104).
   */
  readonly outboxDelivered: boolean;
}

/** Timer abstraction (injected for deterministic tests). */
export interface Scheduler {
  schedule(callback: () => void, delayMs: number): () => void;
}

/** Dependencies and settings of the {@link SyncEngine}. */
export interface SyncEngineOptions {
  readonly store: LocalStore;
  readonly api: SyncApi;
  /** Returns the OIDC subject of the signed-in person, or `null`. */
  readonly currentOwner: () => Promise<string | null>;
  /** Device clock in epoch milliseconds. */
  readonly nowMs: () => number;
  readonly scheduler: Scheduler;
  /** Random number in [0, 1) for the backoff jitter. */
  readonly random: () => number;
  /**
   * Time zone of the device. The snapshot's "yesterday and today" are taken in the time zone of the
   * person's employment stored with the last profile (`RoleSnapshot.timeZoneId`), in this zone only
   * while none is stored (F-18).
   */
  readonly timeZone: () => string;
  /** Maximum events per request (server default 100). */
  readonly maxBatchSize?: number;
  readonly backoff?: BackoffPolicy;
  /**
   * Called after every run that delivered the outbox, complete or failing only when refreshing the
   * snapshot afterwards (F-104), for example to refresh cached queries with what the server accepted.
   */
  readonly onSynced?: () => void;
}

/** Listener for status changes. */
export type SyncStatusListener = (status: SyncStatus) => void;

/**
 * Converts a local event to the request body of `POST /events:batch`. No device identifier is sent
 * (data minimization, spec 3.6); the location only when it was captured with an own event while the
 * employer captures it (T-175). An event on behalf names its person (T-170).
 *
 * @param event - The local event.
 * @returns The request body.
 */
export function toEventRequest(event: LocalEvent): EventRequestBody {
  const body: EventRequestBody = {
    id: event.id,
    employeeId: event.employeeId ?? null,
    kind: event.kind,
    occurredAt: formatInstant(event.occurredAt),
    timeZoneId: event.timeZoneId,
    source: event.source,
    deviceId: null,
    projectId: event.projectId,
    costCenterId: null,
    location: event.location ?? null,
    clientClockOffsetMs: event.clientClockOffsetMs,
    breakQualification: event.breakQualification ?? null,
  };

  return body;
}

/**
 * Converts a local attribution to the request body of `POST /project-attributions:batch`.
 *
 * @param attribution - The local attribution.
 * @returns The request body.
 */
export function toAttributionRequest(attribution: LocalAttribution): AttributionRequestBody {
  const body: AttributionRequestBody = {
    id: attribution.id,
    employeeId: null,
    kind: attribution.kind,
    projectId: attribution.projectId,
    occurredAt: attribution.occurredAt === null ? null : formatInstant(attribution.occurredAt),
    rangeStart: attribution.rangeStart === null ? null : formatInstant(attribution.rangeStart),
    rangeEnd: attribution.rangeEnd === null ? null : formatInstant(attribution.rangeEnd),
    supersedesAttributionId: attribution.supersedesAttributionId,
    note: attribution.note,
    source: attribution.source,
    recordedOffline: attribution.recordedOffline,
    // T-184: only with a project; entries without an activity keep their former shape.
    ...(attribution.projectId === null || (attribution.activityId ?? null) === null
      ? {}
      : { activityId: attribution.activityId ?? null }),
  };

  return body;
}

/**
 * Applies the server result to a submitted attribution, like {@link applyEventResult}.
 *
 * @param attribution - The submitted attribution.
 * @param result - The server result or `undefined`.
 * @param nowMs - Device time.
 * @returns The updated attribution.
 */
export function applyAttributionResult(
  attribution: LocalAttribution,
  result: AttributionResult | undefined,
  nowMs: number,
): LocalAttribution {
  const attempts = attribution.attempts + 1;
  let updated: LocalAttribution;

  if (result === undefined) {
    updated = { ...attribution, attempts };
  } else if (result.status === "Rejected") {
    updated = {
      ...attribution,
      attempts,
      syncStatus: "rejected",
      error: result.error ?? { code: "UNKNOWN", message: "Rejected without reason." },
      hints: result.hints,
      followUp: result.followUp,
    };
  } else {
    updated = {
      ...attribution,
      attempts,
      syncStatus: "synced",
      syncedAtMs: nowMs,
      error: null,
      hints: result.hints,
      followUp: "None",
    };
  }

  return updated;
}

/**
 * Applies the server result to a submitted event (spec 8.1.4): `Accepted` and `Duplicate` confirm
 * it, `Rejected` keeps it visible as a conflict. Without a result the event stays pending.
 *
 * @param event - The submitted event.
 * @param result - The server result or `undefined`.
 * @param nowMs - Device time.
 * @returns The updated event.
 */
export function applyEventResult(
  event: LocalEvent,
  result: EventResult | undefined,
  nowMs: number,
): LocalEvent {
  const attempts = event.attempts + 1;
  let updated: LocalEvent;

  if (result === undefined) {
    updated = { ...event, attempts };
  } else if (result.status === "Rejected") {
    updated = {
      ...event,
      attempts,
      syncStatus: "rejected",
      error: result.error ?? { code: "UNKNOWN", message: "Rejected without reason." },
      hints: result.hints,
      suggestedAction: result.suggestedAction,
    };
  } else {
    updated = {
      ...event,
      attempts,
      syncStatus: "synced",
      syncedAtMs: nowMs,
      error: null,
      hints: result.hints,
      suggestedAction: "None",
    };
  }

  return updated;
}

/**
 * Classifies a failure of a synchronization run.
 *
 * @param error - The error.
 * @returns Phase and error code.
 */
function classifyFailure(error: unknown): { phase: SyncPhase; code: string | null } {
  let classified: { phase: SyncPhase; code: string | null };

  if (error instanceof NetworkError) {
    classified = { phase: "offline", code: null };
  } else if (
    error instanceof AuthenticationRequiredError ||
    (error instanceof ApiError && error.status === 401)
  ) {
    classified = { phase: "signedOut", code: null };
  } else if (error instanceof ApiError) {
    classified = { phase: "error", code: error.code ?? String(error.status) };
  } else {
    classified = { phase: "error", code: error instanceof Error ? error.name : "UNKNOWN" };
  }

  return classified;
}

/**
 * Sends the outbox to the server in order, measures the clock offset and refreshes the offline
 * snapshot (spec 8.1). Runs never overlap; failures are retried with exponential backoff, except
 * when the session ended (then the next trigger after a login resumes).
 */
export class SyncEngine {
  private readonly options: SyncEngineOptions;

  private readonly listeners = new Set<SyncStatusListener>();

  private current: SyncStatus = {
    phase: "idle",
    lastSuccessAtMs: null,
    nextAttemptAtMs: null,
    lastErrorCode: null,
    outboxDelivered: false,
  };

  private running: Promise<void> | null = null;

  private rerunRequested = false;

  private failures = 0;

  private cancelRetry: (() => void) | null = null;

  private disposed = false;

  /**
   * Creates the engine.
   *
   * @param options - Dependencies and settings.
   */
  constructor(options: SyncEngineOptions) {
    this.options = options;
  }

  /**
   * Returns the current status.
   *
   * @returns The status.
   */
  getStatus(): SyncStatus {
    return this.current;
  }

  /**
   * Subscribes to status changes.
   *
   * @param listener - The listener.
   * @returns A function that removes the subscription.
   */
  subscribe(listener: SyncStatusListener): () => void {
    this.listeners.add(listener);
    const unsubscribe = (): void => {
      this.listeners.delete(listener);
    };

    return unsubscribe;
  }

  /**
   * Starts a synchronization. A call during a run schedules exactly one follow-up run, so events
   * recorded meanwhile are sent without delay.
   *
   * @returns A promise that settles when the current run (and its follow-up) finished.
   */
  syncNow(): Promise<void> {
    if (this.disposed) {
      const done = Promise.resolve();
      return done;
    }

    if (this.running !== null) {
      this.rerunRequested = true;
      return this.running;
    }

    this.cancelRetry?.();
    this.cancelRetry = null;
    this.running = this.runLoop().finally(() => {
      this.running = null;
    });

    return this.running;
  }

  /**
   * Stops scheduled retries and ignores further triggers.
   */
  dispose(): void {
    this.disposed = true;
    this.cancelRetry?.();
    this.cancelRetry = null;
    this.listeners.clear();
  }

  private async runLoop(): Promise<void> {
    let again = true;

    while (again) {
      this.rerunRequested = false;
      await this.runOnce();
      again = this.shouldRunAgain();
    }
  }

  /**
   * A follow-up run is needed if a trigger arrived during a successful run.
   *
   * @returns `true` to run again immediately.
   */
  private shouldRunAgain(): boolean {
    const again = this.rerunRequested && !this.disposed && this.current.phase === "idle";

    return again;
  }

  private async runOnce(): Promise<void> {
    const owner = await this.options.currentOwner();

    if (owner === null) {
      this.update({ phase: "signedOut", nextAttemptAtMs: null });
      return;
    }

    this.update({ phase: "syncing", nextAttemptAtMs: null });
    let delivered = false;

    try {
      if (
        this.options.api.canAccessOwnRecords !== undefined &&
        !(await this.options.api.canAccessOwnRecords())
      ) {
        this.failures = 0;
        this.update({ phase: "idle", lastErrorCode: null });
        return;
      }

      await this.measureClockOffset();
      await this.submitPending(owner);
      delivered = true;
      await this.refreshSnapshot(owner);
      const now = this.options.nowMs();
      await this.options.store.putSetting(settingKeys.lastSyncAtMs, String(now));
      this.failures = 0;
      this.update({
        phase: "idle",
        lastSuccessAtMs: now,
        lastErrorCode: null,
        outboxDelivered: true,
      });
      this.options.onSynced?.();
    } catch (error) {
      this.handleFailure(error, delivered);

      // The server accepted the entries; only its state could not be loaded (F-104).
      if (delivered) {
        this.options.onSynced?.();
      }
    }
  }

  /**
   * Measures the offset between server and device clock (spec 8.1.5) as the difference between
   * the server time and the midpoint of the request.
   */
  private async measureClockOffset(): Promise<void> {
    const sentAtMs = this.options.nowMs();
    const serverTime = await this.options.api.serverTime();
    const receivedAtMs = this.options.nowMs();
    const midpointMs = (sentAtMs + receivedAtMs) / 2;
    const offsetMs = Math.round(Date.parse(serverTime) - midpointMs);

    if (!Number.isNaN(offsetMs)) {
      await this.options.store.putSetting(settingKeys.clockOffsetMs, String(offsetMs));
    }
  }

  private async submitPending(owner: string): Promise<void> {
    const maxBatchSize = this.options.maxBatchSize ?? 100;
    const pending = await this.options.store.listPending(owner);

    for (let start = 0; start < pending.length; start += maxBatchSize) {
      const batch = pending.slice(start, start + maxBatchSize);
      const response = await this.options.api.submitBatch(batch.map(toEventRequest));
      const resultsById = new Map(response.results.map((result) => [result.id, result]));
      const nowMs = this.options.nowMs();
      const updated = batch.map((event) =>
        applyEventResult(event, resultsById.get(event.id), nowMs),
      );
      await this.options.store.updateEvents(updated);
    }

    // Attributions follow the clock events of the same run, so a switch never precedes its clock-in.
    const attributions = await this.waitingForTheirEvents(
      owner,
      await this.options.store.listPendingAttributions(owner),
    );

    for (let start = 0; start < attributions.length; start += maxBatchSize) {
      const batch = attributions.slice(start, start + maxBatchSize);
      const response = await this.options.api.submitAttributions(batch.map(toAttributionRequest));
      const resultsById = new Map(response.results.map((result) => [result.id, result]));
      const nowMs = this.options.nowMs();
      const updated = batch.map((attribution) =>
        applyAttributionResult(attribution, resultsById.get(attribution.id), nowMs),
      );
      await this.options.store.updateAttributions(updated);
    }
  }

  /**
   * Holds back attributions whose clock event is not accepted yet and discards those whose clock event the server
   * rejected, for example the switch with the activity of a clock-in that a device with an old state recorded (T-184).
   *
   * @param owner - Owner key.
   * @param pending - The pending attributions.
   * @returns The attributions that can be sent now.
   */
  private async waitingForTheirEvents(
    owner: string,
    pending: readonly LocalAttribution[],
  ): Promise<LocalAttribution[]> {
    if (pending.every((attribution) => attribution.followsEventId === undefined)) {
      const all = [...pending];
      return all;
    }

    const events = new Map(
      (await this.options.store.listEvents(owner)).map((event) => [event.id, event]),
    );
    const statusOf = (attribution: LocalAttribution): LocalEvent["syncStatus"] | null =>
      attribution.followsEventId === undefined
        ? null
        : (events.get(attribution.followsEventId)?.syncStatus ?? null);
    const orphaned = pending.filter((attribution) => statusOf(attribution) === "rejected");

    for (const attribution of orphaned) {
      await this.options.store.updateAttributions([
        {
          ...attribution,
          syncStatus: "rejected",
          error: { code: "CLOCK-EVENT-REJECTED", message: "The clock event was rejected." },
        },
      ]);
      await this.options.store.dismissRejectedAttribution(attribution.id);
    }

    const ready = pending.filter((attribution) => {
      const status = statusOf(attribution);
      return status === null || status === "synced";
    });

    return ready;
  }

  private async refreshSnapshot(owner: string): Promise<void> {
    const fetchedAtMs = this.options.nowMs();
    const timeZone = await this.snapshotTimeZone(owner, fetchedAtMs);
    const today = toLocalDate(Math.floor(fetchedAtMs / 1000), timeZone);
    const yesterday = addDays(today, -1);
    const [clockState, recentDays, myProjects, recentProjectDays, previous, events] =
      await Promise.all([
        this.options.api.clockState(),
        this.options.api.workDays(yesterday, today),
        this.options.api.myProjects(),
        this.options.api.projectDays(yesterday, today),
        this.options.store.getSnapshot(owner),
        this.options.store.listEvents(owner),
      ]);
    // A shift closed meanwhile that began before yesterday stays loaded until its end leaves the range (F-104).
    const carried = carriedSnapshotDays(previous?.days ?? [], events, yesterday, timeZone);
    const [earlier, later] = await Promise.all([
      this.loadDays(
        snapshotRangeStart(today, [...clockState.openDays, ...carried]),
        addDays(yesterday, -1),
      ),
      this.loadDays(addDays(today, 1), snapshotRangeEnd(today, clockState.openDays)),
    ]);
    const runningBreakQualification = await this.runningBreakQualification(owner, clockState);
    await this.options.store.putSnapshot({
      ownerSubject: owner,
      fetchedAtMs,
      clockState,
      days: [...earlier.days, ...recentDays, ...later.days],
      projectDays: [...earlier.projectDays, ...recentProjectDays, ...later.projectDays],
      runningBreakQualification,
    });
    await this.options.store.putProjectCache({ ownerSubject: owner, fetchedAtMs, myProjects });
    await this.options.store.pruneSynced(owner, fetchedAtMs);
    await this.options.store.pruneSyncedAttributions(owner, fetchedAtMs - attributionDisplayMs);
  }

  /**
   * Returns the declared kind of the break the server reports running, from the break start this
   * device recorded at that instant. The days of a snapshot requested in the second the break began
   * do not contain it, and the event is pruned with the snapshot (F-57); a later refresh of the same
   * break therefore keeps the kind of the previous snapshot.
   *
   * @param owner - Owner key.
   * @param clockState - Clock state of the server.
   * @returns The kind, or `null` if no break runs or this device did not record its start.
   */
  private async runningBreakQualification(
    owner: string,
    clockState: ClockStateResponse,
  ): Promise<BreakQualification | null> {
    if (clockState.state !== "OnBreak" || clockState.since === null) {
      return null;
    }

    const since = parseInstant(clockState.since);
    const events = await this.options.store.listEvents(owner);
    const start = events.find(
      (event) =>
        isOwnEvent(event) &&
        event.kind === "BreakStart" &&
        event.syncStatus === "synced" &&
        event.occurredAt === since,
    );

    if (start !== undefined) {
      const recorded = start.breakQualification ?? null;

      return recorded;
    }

    const previous = await this.options.store.getSnapshot(owner);
    const sameBreak =
      previous !== null &&
      previous.clockState.state === "OnBreak" &&
      previous.clockState.since !== null &&
      parseInstant(previous.clockState.since) === since;
    const kept = sameBreak ? (previous.runningBreakQualification ?? null) : null;

    return kept;
  }

  /**
   * Returns the time zone the snapshot's days are taken in: the employment's from the last profile
   * stored on this device if it is usable (not older than the role snapshots may be, a zone the device
   * knows), else the device's (F-18). A failing local store falls back to the device.
   *
   * @param owner - Owner key of the signed-in person.
   * @param nowMs - Device time of the synchronization in milliseconds.
   * @returns The IANA time zone.
   */
  private async snapshotTimeZone(owner: string, nowMs: number): Promise<string> {
    const stored = await this.options.store.getRoleSnapshot(owner).catch(() => null);
    const timeZone = storedWorkTimeZone(stored, owner, nowMs) ?? this.options.timeZone();

    return timeZone;
  }

  /**
   * Loads the days of a range outside "yesterday and today": before them for a shift still open from
   * an earlier day (F-11), after them for an open day the device's "today" has not reached (F-18).
   * The usual run has no such range and keeps its parallel requests.
   *
   * @param from - First day of the range.
   * @param to - Last day of the range; before `from` for an empty range.
   * @returns The working days and project days of the range.
   */
  private async loadDays(
    from: LocalDate,
    to: LocalDate,
  ): Promise<{ days: readonly WorkDayResponse[]; projectDays: readonly ProjectDay[] }> {
    if (from > to) {
      const none = { days: [], projectDays: [] };
      return none;
    }

    const [days, projectDays] = await Promise.all([
      this.options.api.workDays(from, to),
      this.options.api.projectDays(from, to),
    ]);
    const loaded = { days, projectDays };

    return loaded;
  }

  private handleFailure(error: unknown, delivered: boolean): void {
    const { phase, code } = classifyFailure(error);

    if (phase === "signedOut") {
      this.update({
        phase,
        nextAttemptAtMs: null,
        lastErrorCode: null,
        outboxDelivered: delivered,
      });
      return;
    }

    this.failures += 1;
    const policy = this.options.backoff ?? defaultBackoffPolicy;
    const delayMs = backoffDelayMs(this.failures, policy, this.options.random());
    const nextAttemptAtMs = this.options.nowMs() + delayMs;
    this.cancelRetry = this.options.scheduler.schedule(() => {
      this.cancelRetry = null;
      void this.syncNow();
    }, delayMs);
    this.update({ phase, nextAttemptAtMs, lastErrorCode: code, outboxDelivered: delivered });
  }

  private update(changes: Partial<SyncStatus>): void {
    this.current = { ...this.current, ...changes };

    for (const listener of this.listeners) {
      listener(this.current);
    }
  }
}
